<script setup lang="ts">
import type { ResolvedMonospacePrimitiveField } from '#layers/data-model/public';
import { computed, ref } from 'vue';
import { provideBlockInjection } from '#layers/block-engine/public';
import { getPrimitiveFieldInputBlockOptions, useDataModelDisplayNames } from '#layers/data-model/public';
import {
	injectFormBlockContext,
	PrimitiveFieldLabelMenu,
	providePrimitiveFormFieldContext,
	useFormFieldChanges,
	useInputBlockDisabled,
} from '#layers/forms/public';

const props = withDefaults(defineProps<{
	primitiveField?: ResolvedMonospacePrimitiveField;
	label?: string;
	description?: string;
	name?: string;
	/** Overrides whether the form's write requires the field. */
	required?: boolean;
	ejected?: boolean;
}>(), { required: undefined });

const { getPrimitiveFieldDisplayName } = useDataModelDisplayNames();
const label = computed(() => props.label ?? (props.primitiveField ? getPrimitiveFieldDisplayName(props.primitiveField) : ''));
const description = computed(() => props.description ?? props.primitiveField?.meta?.description ?? undefined);
const name = computed(() => props.name ?? props.primitiveField?.apiName);

const formBlock = injectFormBlockContext(null);
const required = computed(() => {
	const field = props.primitiveField;
	return props.required ?? (field && formBlock ? formBlock.writeContext.value.requiresPrimitiveField(field) : false);
});

const { hasChanges, originalValue } = useFormFieldChanges(formBlock, name);

const fallbackModel = ref<unknown>();

const stateModel = computed({
	get: () => name.value ? formBlock?.state.value?.[name.value] : fallbackModel.value,
	set: (value) => {
		if (name.value && formBlock?.state.value) {
			formBlock.state.value[name.value] = value;
		}
		else {
			fallbackModel.value = value;
		}
	},
});

const inputBlockProps = computed(() => {
	if (!props.primitiveField) {
		return {
			block: '$builtin:Input',
		};
	}

	return getPrimitiveFieldInputBlockOptions(props.primitiveField);
});

const inputBlockDisabled = useInputBlockDisabled(inputBlockProps);

const { model, disabled } = providePrimitiveFormFieldContext({
	model: stateModel,
	field: () => props.primitiveField,
	disabled: inputBlockDisabled,
});

provideBlockInjection({
	model,
});

const showLabelMenu = computed(() => !!props.primitiveField?.type && label.value);
</script>

<template>
	<DFormField
		:name
		:label
		:description
		:required
		:dirty="hasChanges"
		:ui="{
			labelWrapper: showLabelMenu ? 'pe-4' : undefined,
		}"
	>
		<!-- Trigger renders OUTSIDE the <Label> element via label-trailing slot -->
		<template v-if="showLabelMenu" #label-trailing>
			<PrimitiveFieldLabelMenu
				v-model="model"
				:label
				:primitive-field
				:disabled
				:dirty="hasChanges"
				:original-value
			/>
		</template>
		<template v-if="!ejected && formBlock?.collection.value && inputBlockProps">
			<BlockRender v-bind="inputBlockProps" />
		</template>
		<slot v-else />
	</DFormField>
</template>
